import { ArrowDownTrayIcon, ArrowLeftIcon } from '@heroicons/react/24/solid';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SelectMe from 'react-select-me';

import { formatCurrency, formatValue } from '../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { BoitageAssignment, BoitageWindow } from '../../../@types/Boitage';
import { BOITAGE_STATUS   } from '../../../@types/Boitage';
import type { Company } from '../../../@types/Company';
import { useDvfYears } from '../../../hooks/useDvfYears';
import type { RootState } from '../../../store/store';
import { getCompanyBoitageAssignments, getCompanyBoitageWindows, patchCompanyBoitageWindow } from '../../../utils/api';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable/MUITable.module';
import { showToast } from '../../common/Notification';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';
import TransactionBoitageModelButton from './components/TransactionBoitageModelButton.module';

type Filters = {
  ventesMin: number | null;
  ventesMax: number | null;
  caMin: number | null;
  caMax: number | null;
  commMin: number | null;
  commMax: number | null;
  avgSaleMin: number | null;
  scisMin: number | null;
  transactionnaire: string | null;
};

type CoproSalesRow = {
  id: number;
  immatriculation: string;
  name: string;
  address: string;
  cp: string;
  city: string;
  salesAmount: number;
  nbLots: number;
  nbSales: number;
  m2ForNbSales: number | null;
  salesAvgForM2: number | null;
};

type CoproSalesApiRow = {
  id: number;
  immatriculation?: string;
  name?: string;
  address?: string;
  cp?: string;
  city?: string;
  salesAmount?: number;
  nbLots?: number;
  nbSales?: number;
  m2ForNbSales?: number | null;
  salesAvgForM2?: number | null;
};

type BoitageActivation = { 'Boîtage 1': boolean; 'Boîtage 2': boolean } | null;

type AttributionListItem = {
  fonctionType: string | number;
  copro?: { id?: number };
  user?: { firstname?: string; lastname?: string };
};

const TRANSACTIONNAIRE_FONCTION_TYPE = 3; // see FonctionTypeUserEnum.TRANSACTIONNAIRE
const GESTIONNAIRE_FONCTION_TYPE = 1; // see attribution fonctionType for gestionnaire

function BoitageTransaction() {
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);

  const { yearsOptions } = useDvfYears({ startYear: 2014 });

  const [rows, setRows] = useState<CoproSalesRow[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sciCountByCopro, setSciCountByCopro] = useState<Record<number, number>>({});
  const [transactionnaireByCopro, setTransactionnaireByCopro] = useState<Record<number, string>>({});
  const [gestionnaireByCopro, setGestionnaireByCopro] = useState<Record<number, string>>({});
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>({ ventesMin: null, ventesMax: null, caMin: null, caMax: null, commMin: null, commMax: null, avgSaleMin: null, scisMin: null, transactionnaire: null });
  const [commissionRate] = useState<number>(company?.commission ?? 5);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [assignmentsByCoproId, setAssignmentsByCoproId] = useState<Record<number, { 1?: { plannedLabel: string | null }, 2?: { plannedLabel: string | null } }>>({});
  const [bulkQualCriteria, setBulkQualCriteria] = useState<{ highVolume: boolean; highValue: boolean; manySci: boolean; highCommission: boolean }>({ highVolume: false, highValue: false, manySci: false, highCommission: false });
  const [isExportingBoitage1, setIsExportingBoitage1] = useState(false);
  const [isExportingBoitage2, setIsExportingBoitage2] = useState(false);

  const headers = [
    { title: 'Immatriculation' },
    { title: 'Nom' },
    { title: 'Adresse' },
    { title: 'Ville' },
    { title: 'Montant total des ventes' },
    { title: 'Commission' },
    { title: 'Nb lots hab / local pro' },
    { title: 'Nb ventes' },
    { title: 'm2 moyen / vente' },
    { title: 'Prix moyen / m2' },
    { title: 'Nb SCI' },
    { title: 'Gestionnaire' },
    { title: 'Transactionnaire' },
  ];

  const fetchRows = async() => {
    if (!company?.id || !jwt || selectedYears.length === 0) {return;}
    setStatus('PENDING');
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      selectedYears.forEach((y) => params.append('years[]', String(y)));
      const resp = await fetch(`/api/companies/${company.id}/stats/copros/sales/years?${params.toString()}`, {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data = await resp.json();
      const results = data.results || data;
      const mapped: CoproSalesRow[] = (Array.isArray(results) ? results : []).map((r: CoproSalesApiRow) => ({
        id: r.id,
        immatriculation: r.immatriculation,
        name: r.name,
        address: r.address,
        cp: r.cp,
        city: r.city,
        salesAmount: Number(r.salesAmount || 0),
        nbLots: Number(r.nbLots || 0),
        nbSales: Number(r.nbSales || 0),
        m2ForNbSales: r.m2ForNbSales != null ? Number(r.m2ForNbSales) : null,
        salesAvgForM2: r.salesAvgForM2 != null ? Number(r.salesAvgForM2) : null,
      }));
      setRows(mapped);
      setStatus('SUCCESS');
    } catch (e) {
      console.error('Error fetching sales rows :', e);
      setStatus('ERROR');
      setErrorMessage('Une erreur est survenue lors du chargement des données.');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  };

  const [windows, setWindows] = useState<BoitageWindow[] | null>(null);
  const fetchWindows = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(getCompanyBoitageWindows(company.id), JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data: BoitageWindow[] = await resp.json();


      setWindows(data);
    } catch (e) {
      console.error('Error fetching windows :', e);
    }
  };

  useEffect(() => { fetchWindows(); }, [company?.id, jwt]);

  // Activation flags from planning (current year with fallback)
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const activation = useMemo((): BoitageActivation => {
    const timeline = (company as Company | null)?.toolsTimeline ?? null;
    if (!timeline) { return null; }
    const years = Object.keys(timeline)
      .map((k) => Number(k))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
    const candidates: number[] = [];
    if (years.includes(currentYear)) { candidates.push(currentYear); }
    const past = years.filter((y) => y < currentYear).sort((a, b) => b - a);
    const future = years.filter((y) => y > currentYear).sort((a, b) => a - b);
    candidates.push(...past, ...future);
    for (const y of candidates) {
      const yd = timeline[String(y)];
      if (!yd || typeof yd !== 'object') { continue; }
      const eventActivation = yd.eventActivation;
      const boitage = eventActivation?.boitage;
      if (boitage && Object.prototype.hasOwnProperty.call(boitage, 'Boîtage 1') && Object.prototype.hasOwnProperty.call(boitage, 'Boîtage 2')) {
        return {
          'Boîtage 1': Boolean(boitage['Boîtage 1']),
          'Boîtage 2': Boolean(boitage['Boîtage 2']),
        };
      }
    }
    return null;
  }, [company, currentYear]);
  const isBoitage1Active = activation ? activation['Boîtage 1'] : true;
  const isBoitage2Active = activation ? activation['Boîtage 2'] : true;

  useEffect(() => {
    if (yearsOptions.length > 0 && selectedYears.length === 0) {
      const firstTwo = yearsOptions.slice(0, 2).map((o) => Number(o.value));
      setSelectedYears(firstTwo);
    }
  }, [yearsOptions, selectedYears.length]);

  // Fetch assignments for visible windows and create a mapping coproId -> { 1?: {plannedLabel}, 2?: {plannedLabel} }
  useEffect(() => {
    const run = async() => {
      if (!company?.id || !jwt || !windows) { return; }
      try {
        const results = await Promise.all(windows.map(async(w) => {
          const r = await fetch(getCompanyBoitageAssignments(company.id, Number(w.year), w.eventIndex), JWTHeader(jwt));
          if (!r.ok) { throw new Error(`${r.status}`); }
          const json: { results: BoitageAssignment[] } = await r.json();
          return { w, rows: json.results.map(a => ({ coproId: a.coproId, eventIndex: a.eventIndex as 1 | 2, plannedLabel: a.plannedLabel ?? null })) };
        }));
        const map: Record<number, { 1?: { plannedLabel: string | null }, 2?: { plannedLabel: string | null } }> = {};
        results.forEach((bucket) => {
          bucket.rows.forEach((a) => {
            if (!map[a.coproId]) { map[a.coproId] = {}; }
            if (a.eventIndex === 1) { map[a.coproId][1] = { plannedLabel: a.plannedLabel }; }
            if (a.eventIndex === 2) { map[a.coproId][2] = { plannedLabel: a.plannedLabel }; }
          });
        });
        setAssignmentsByCoproId(map);
      } catch (e) {
        console.error('Error fetching assignments :', e);
      }
    };
    run();
  }, [windows, company?.id, jwt]);

  // Additional maps for filters parity with detailed list
  const fetchSciCounts = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(`/api/companies/${company.id}/copros/scis/stats`, JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data: Array<{ id: number; scis: { total: number } }> = await resp.json();
      const map: Record<number, number> = {};
      data.forEach((c) => { map[c.id] = c.scis?.total ?? 0; });
      setSciCountByCopro(map);
    } catch (e) {
      console.error('Error fetching SCI counts :', e);
    }
  };

  const fetchTransactionnaires = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(`/api/companies/${company.id}/validated-copros/attributions`, JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data = await resp.json();
      const map: Record<number, string> = {};
      (data?.results || []).forEach((a: AttributionListItem) => {
        if (a?.fonctionType === TRANSACTIONNAIRE_FONCTION_TYPE && a?.copro?.id && a?.user) {
          map[a.copro.id] = `${a.user.firstname} ${a.user.lastname ? a.user.lastname.toUpperCase() : ''}`;
        }
      });
      setTransactionnaireByCopro(map);
    } catch (e) {
      console.error('Error fetching attributions :', e);
    }
  };

  const fetchGestionnaires = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(`/api/companies/${company.id}/validated-copros/attributions`, JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data = await resp.json();
      const map: Record<number, string> = {};
      (data?.results || []).forEach((a: AttributionListItem) => {
        if (a?.fonctionType === GESTIONNAIRE_FONCTION_TYPE && a?.copro?.id && a?.user) {
          map[a.copro.id] = `${a.user.firstname} ${a.user.lastname ? a.user.lastname.toUpperCase() : ''}`;
        }
      });
      setGestionnaireByCopro(map);
    } catch (e) {
      console.error('Error fetching gestionnaires :', e);
    }
  };

  const handleGoBack = () => {
    try { navigate(-1); } catch { navigate(ROUTES.HOME); }
  };

  // const handleTemplateSelect = (template: 'standard' | 'premium') => {
  //   setSelectedTemplate(template);
  //   setShowTemplatePopup(false);
  //   showToast('success', `Modèle "${template === 'standard' ? 'Standard' : 'Premium'}" sélectionné pour le boîtage.`);
  // };

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    const rate = commissionRate / 100;
    return rows.filter((r) => {
      // Only show copros with at least one boitage assignment
      const hasBoitage = assignmentsByCoproId[r.id] &&
        (assignmentsByCoproId[r.id][1] || assignmentsByCoproId[r.id][2]);
      if (!hasBoitage) { return false; }

      const commission = r.salesAmount * rate;
      const tx = transactionnaireByCopro[r.id] || '';
      const sciCount = sciCountByCopro[r.id] ?? 0;
      const avgSale = r.nbSales > 0 ? (r.salesAmount / r.nbSales) : 0;
      const basic = `${r.immatriculation} ${r.name} ${r.address} ${r.city} ${r.cp}`.toLowerCase();
      if (q && !basic.includes(q)) { return false; }
      if (filters.ventesMin != null && r.nbSales < filters.ventesMin) { return false; }
      if (filters.ventesMax != null && r.nbSales > filters.ventesMax) { return false; }
      if (filters.caMin != null && r.salesAmount < filters.caMin) { return false; }
      if (filters.caMax != null && r.salesAmount > filters.caMax) { return false; }
      if (filters.commMin != null && commission < filters.commMin) { return false; }
      if (filters.commMax != null && commission > filters.commMax) { return false; }
      if (filters.avgSaleMin != null && avgSale < filters.avgSaleMin) { return false; }
      if (filters.scisMin != null && sciCount < filters.scisMin) { return false; }
      if (filters.transactionnaire && tx !== filters.transactionnaire) { return false; }
      return true;
    });
  }, [rows, search, filters, commissionRate, transactionnaireByCopro, sciCountByCopro, assignmentsByCoproId]);

  const dataForTable = useMemo(() => {
    const rate = commissionRate / 100;
    return filteredRows.map((r) => ({
      'Immatriculation': r.immatriculation,
      'Nom': (
        <div className="flex items-center gap-2">
          <RedirectionCoproButtons coproId={r.id} />
          <span className="font-semibold">{r.name}</span>
        </div>
      ),
      'Adresse': r.address,
      'Ville': `${r.city} - ${r.cp}`,
      'Montant total des ventes': formatCurrency(r.salesAmount),
      'Commission': formatCurrency(r.salesAmount * rate),
      'Nb lots hab / local pro': r.nbLots,
      'Nb ventes': r.nbSales,
      'm2 moyen / vente': r.m2ForNbSales != null ? formatValue(r.m2ForNbSales) : '-',
      'Prix moyen / m2': r.salesAvgForM2 != null ? formatCurrency(r.salesAvgForM2) : '-',
      'Nb SCI': sciCountByCopro[r.id] ?? '-',
      'Gestionnaire': (
        <span className="text-xs font-medium text-blue-600">{gestionnaireByCopro[r.id] || ''}</span>
      ),
      'Transactionnaire': (
        <span className="text-xs font-medium text-purple-600">{transactionnaireByCopro[r.id] || ''}</span>
      ),
      'Boîtage 1': (() => {
        const a = assignmentsByCoproId[r.id];
        const isAssigned = Boolean(a && a[1]);
        const targetWindow = windows?.find(w => w.eventIndex === 1);
        const label = (a && a[1]?.plannedLabel) || (targetWindow?.plannedLabel) || '';
        const year = targetWindow?.plannedDate ? new Date(`${targetWindow.plannedDate}T00:00:00`).getFullYear() : (targetWindow?.year || '');
        const suffix = label ? `${label}${year ? ` ${year}` : ''}` : '';
        const text = suffix ? (isAssigned ? `Programmé pour ${suffix}` : `Non inclu pour ${suffix}`) : (isAssigned ? 'Programmé' : 'Non inclu');
        return (
          <div
            className={`rounded border px-2 py-1 text-xs font-medium ${
              isAssigned
                ? 'border-green-300 bg-green-100 text-green-700'
                : 'border-red-300 bg-red-100 text-red-700'
            }`}
          >
            {text}
          </div>
        );
      })(),
      'Boîtage 2': (() => {
        const a = assignmentsByCoproId[r.id];
        const isAssigned = Boolean(a && a[2]);
        const targetWindow = windows?.find(w => w.eventIndex === 2);
        const label = (a && a[2]?.plannedLabel) || (targetWindow?.plannedLabel) || '';
        const year = targetWindow?.plannedDate ? new Date(`${targetWindow.plannedDate}T00:00:00`).getFullYear() : (targetWindow?.year || '');
        const suffix = label ? `${label}${year ? ` ${year}` : ''}` : '';
        const text = suffix ? (isAssigned ? `Programmé pour ${suffix}` : `Non inclu pour ${suffix}`) : (isAssigned ? 'Programmé' : 'Non inclu');
        return (
          <div
            className={`rounded border px-2 py-1 text-xs font-medium ${
              isAssigned
                ? 'border-green-300 bg-green-100 text-green-700'
                : 'border-red-300 bg-red-100 text-red-700'
            }`}
          >
            {text}
          </div>
        );
      })(),
    }));
  }, [filteredRows, commissionRate, sciCountByCopro, gestionnaireByCopro, transactionnaireByCopro, assignmentsByCoproId, windows]);

  // Local button with follow-mouse tooltip when disabled
  const MarkDoneButton: React.FC<{ plannedDate: string; executedAt?: string | null; onSubmit: (executedIso: string) => Promise<void> | void }> = ({ plannedDate, executedAt, onSubmit }) => {
    const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number }>({ visible: false, x: 0, y: 0 });
    const planned = useMemo(() => new Date(`${plannedDate}T00:00:00`), [plannedDate]);
    const start = useMemo(() => { const d = new Date(planned); d.setDate(d.getDate() - 15); return d; }, [planned]);
    const end = useMemo(() => { const d = new Date(planned); d.setDate(d.getDate() + 30); return d; }, [planned]);
    const inWindow = useMemo(() => { const now = new Date(); return now >= start && now <= end; }, [start, end]);
    const [selectedDate, setSelectedDate] = useState<string>(() => executedAt ? executedAt.slice(0, 10) : plannedDate);

    useEffect(() => {
      setSelectedDate(executedAt ? executedAt.slice(0, 10) : plannedDate);
    }, [executedAt, plannedDate]);

    const handleMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
      if (inWindow) { return; }
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      setTooltip({ visible: true, x: e.clientX - rect.left + 12, y: e.clientY - rect.top + 12 });
    };

    const handleMouseLeave = () => setTooltip((t) => ({ ...t, visible: false }));

    if (executedAt) {
      const formatted = new Date(executedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      return (
        <span className="text-xs font-semibold text-green-700 uppercase">EFFECTUÉ LE {formatted}</span>
      );
    }

    const handleSubmit = () => {
      if (!inWindow) { return; }
      if (!selectedDate) {
        showToast('warning', 'Sélectionnez une date.');
        return;
      }
      const iso = new Date(`${selectedDate}T00:00:00`).toISOString();
      onSubmit(iso);
    };

    return (
      <div className="relative inline-flex items-center gap-2" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseMove}>
        <input
          type="date"
          className={`rounded border px-2 py-1 text-xs transition-colors ${inWindow ? 'bg-white text-slate-700' : 'cursor-not-allowed bg-gray-100 text-gray-400'}`}
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          disabled={!inWindow}
        />
        <button
          className={`rounded px-3 py-1.5 text-xs text-white transition-colors ${inWindow ? 'bg-green-600 hover:bg-green-700' : 'cursor-not-allowed bg-gray-300'}`}
          disabled={!inWindow}
          onClick={handleSubmit}
        >
          Marquer comme effectué
        </button>
        {!inWindow && tooltip.visible && (
          <div
            className="pointer-events-none absolute z-10 rounded bg-gray-800 px-2 py-1 text-[11px] whitespace-nowrap text-white shadow-md"
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            Vous n'etes pas encore dans cette fenetre de distribution
          </div>
        )}
      </div>
    );
  };

  useEffect(() => { fetchRows(); fetchSciCounts(); fetchTransactionnaires(); fetchGestionnaires(); }, [company?.id, jwt, selectedYears.join(',')]);

  const resetFilters = () => {
    setFilters({ ventesMin: null, ventesMax: null, caMin: null, caMax: null, commMin: null, commMax: null, avgSaleMin: null, scisMin: null, transactionnaire: null });
    setBulkQualCriteria({ highVolume: false, highValue: false, manySci: false, highCommission: false });
  };

  const computeCountWithFilters = useCallback((candidate: Filters) => {
    const q = search.toLowerCase();
    const rate = commissionRate / 100;
    let n = 0;
    for (const r of rows) {
      const commission = r.salesAmount * rate;
      const tx = transactionnaireByCopro[r.id] || '';
      const sciCount = sciCountByCopro[r.id] ?? 0;
      const avgSale = r.nbSales > 0 ? (r.salesAmount / r.nbSales) : 0;
      const basic = `${r.immatriculation} ${r.name} ${r.address} ${r.city} ${r.cp}`.toLowerCase();
      if (q && !basic.includes(q)) { continue; }
      if (candidate.ventesMin != null && r.nbSales < candidate.ventesMin) { continue; }
      if (candidate.ventesMax != null && r.nbSales > candidate.ventesMax) { continue; }
      if (candidate.caMin != null && r.salesAmount < candidate.caMin) { continue; }
      if (candidate.caMax != null && r.salesAmount > candidate.caMax) { continue; }
      if (candidate.commMin != null && commission < candidate.commMin) { continue; }
      if (candidate.commMax != null && commission > candidate.commMax) { continue; }
      if (candidate.avgSaleMin != null && avgSale < candidate.avgSaleMin) { continue; }
      if (candidate.scisMin != null && sciCount < candidate.scisMin) { continue; }
      if (candidate.transactionnaire && tx !== candidate.transactionnaire) { continue; }
      n += 1;
    }
    return n;
  }, [rows, search, commissionRate, transactionnaireByCopro, sciCountByCopro]);

  const toggleQualificationCriterion = (key: 'highVolume' | 'highValue' | 'manySci' | 'highCommission') => {
    setBulkQualCriteria((current) => {
      const updated = { ...current, [key]: !current[key] };
      setFilters((prev) => {
        const next: Filters = {
          ...prev,
          ventesMin: null,
          avgSaleMin: null,
          scisMin: null,
          commMin: null,
        };
        if (updated.highVolume) { next.ventesMin = Math.max(next.ventesMin ?? 0, 21); }
        if (updated.highValue) { next.avgSaleMin = Math.max(next.avgSaleMin ?? 0, 500000); }
        if (updated.manySci) { next.scisMin = Math.max(next.scisMin ?? 0, 15); }
        if (updated.highCommission) { next.commMin = Math.max(next.commMin ?? 0, 500000); }
        const count = computeCountWithFilters(next);
        showToast('success', `Qualification appliquée ! ${count} copropriété(s) qualifiée(s) pour le boîtage.`);
        return next;
      });
      return updated;
    });
  };

  const handleExportBoitage = useCallback(async(eventIndex: 1 | 2) => {
    const setExporting = eventIndex === 1 ? setIsExportingBoitage1 : setIsExportingBoitage2;
    setExporting(true);
    try {
      // Filter rows that have the specified boitage assignment
      const rowsToExport = filteredRows.filter((r) => {
        const a = assignmentsByCoproId[r.id];
        return Boolean(a && a[eventIndex]);
      });

      if (rowsToExport.length === 0) {
        showToast('warning', `Aucune copropriété assignée au Boîtage ${eventIndex}.`);
        setExporting(false);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(`Boîtage ${eventIndex}`);

      // Add header row
      worksheet.addRow([
        'Nom',
        'Nb documents',
        'Gestionnaire',
        'Transactionnaire',
        'Adresse',
        'Ville',
        'Commission',
        'Nb ventes',
      ]);

      // Style the header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
      };

      const rate = commissionRate / 100;

      // Add data rows
      rowsToExport.forEach((r) => {
        const commission = r.salesAmount * rate;
        worksheet.addRow([
          r.name || '',
          r.nbLots || 0,
          gestionnaireByCopro[r.id] || '',
          transactionnaireByCopro[r.id] || '',
          r.address || '',
          r.city || '',
          commission,
          r.nbSales || 0,
        ]);
      });

      // Auto-fit columns
      worksheet.columns.forEach((column) => {
        let maxLength = 10;
        column.eachCell?.({ includeEmpty: true }, (cell) => {
          const cellValue = cell.value ? String(cell.value) : '';
          maxLength = Math.max(maxLength, cellValue.length + 2);
        });
        column.width = Math.min(maxLength, 50);
      });

      // Format commission column as currency
      const commissionCol = worksheet.getColumn(7);
      commissionCol.numFmt = '#,##0.00 €';

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Boitage_${eventIndex}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(link.href);
      link.remove();

      showToast('success', `Export Boîtage ${eventIndex} terminé (${rowsToExport.length} copropriété(s)).`);
    } catch (error) {
      console.error('Error exporting boitage :', error);
      showToast('error', "Une erreur est survenue lors de l'export.");
    } finally {
      setExporting(false);
    }
  }, [filteredRows, assignmentsByCoproId, commissionRate, gestionnaireByCopro, transactionnaireByCopro]);

  return (
    <div className="rounded-lg bg-white p-4 shadow-md md:p-8">
      <div className="mb-4 flex items-center justify-between">
        <button onClick={() => handleGoBack()} className="flex items-center gap-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark">
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Retour</span>
        </button>
      </div>

      <div className="mb-6 rounded-2xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold md:text-3xl">BOÎTAGE TRANSACTION</h1>
            <div className="opacity-90">Planifiez vos boîtages avec les fenêtres issues du planning</div>
          </div>
          <div className="flex items-center gap-3">
            <TransactionBoitageModelButton />
          </div>
        </div>
      </div>

      {/* Model managed in Qualification; no upload/demo here */}

      {/* Removed old TODO section now implemented */}

      {/* Inline filters (parity with detailed list) */}
      <div className="mb-3 rounded-lg border bg-white shadow-sm">
        <div className="p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-6">
            <div className="flex items-start gap-2">
              <label className="mt-2 text-sm font-medium text-slate-600"><span role="img" aria-label="magnifying glass">🔍</span> Rechercher :</label>
              <input className="w-64 rounded border px-3 py-2 text-sm" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, ville, immatriculation..." />
            </div>
            <div className="flex items-start gap-2">
              <label className="mt-2 text-sm font-medium text-slate-600"><span role="img" aria-label="calendar">📅</span> Années :</label>
              <div className="min-w-[220px]">
                <SelectMe
                  id="years-select"
                  name="years"
                  value={selectedYears.map((y) => ({ value: String(y), label: String(y) }))}
                  onChange={(selectedOptions) => setSelectedYears(selectedOptions.map((o) => Number(o.value)))}
                  options={yearsOptions}
                  multiple
                  closeOnSelect={false}
                  listPosition="bottom"
                  placeholder="Sélectionnez une ou plusieurs années"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 rounded border p-4" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderColor: '#3b82f6' }}>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="chart">📊</span> Nb ventes:</label>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.ventesMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, ventesMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min" />
                <span>à</span>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.ventesMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, ventesMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="emoji">💰</span> CA:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.caMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, caMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
                <span>à</span>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.caMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, caMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="target">🎯</span> Commission:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.commMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, commMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
                <span>à</span>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.commMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, commMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="person">👤</span> Transactionnaire:</label>
                <select className="w-40 rounded border px-2 py-1 text-sm" value={filters.transactionnaire ?? ''} onChange={(e) => setFilters((f) => ({ ...f, transactionnaire: e.target.value || null }))}>
                  <option value="">Tous</option>
                  {[...new Set(Object.values(transactionnaireByCopro).filter(Boolean))].map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="emoji">🏷️</span> Prix moyen/vente:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.avgSaleMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, avgSaleMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="factory">🏢</span> Nb SCI min:</label>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.scisMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, scisMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min" />
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button className="rounded bg-slate-500 px-3 py-1.5 text-sm text-white hover:bg-slate-600" onClick={resetFilters}><span role="img" aria-label="refresh">🔄</span> Réinitialiser</button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-4">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="px-3 text-sm font-medium text-gray-600">OU</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>

            <div className="mt-4">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700"><span role="img" aria-label="target">🎯</span> Filtre de qualification prédéfini :</span>
                <span className="text-xs text-slate-500">Identifiez automatiquement les copropriétés prioritaires pour le boîtage</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button className={`rounded-2xl border px-3 py-1.5 text-xs ${bulkQualCriteria.highVolume ? 'border-blue-700 bg-blue-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`} onClick={() => toggleQualificationCriterion('highVolume')}>Fort volume</button>
                <button className={`rounded-2xl border px-3 py-1.5 text-xs ${bulkQualCriteria.highValue ? 'border-blue-700 bg-blue-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`} onClick={() => toggleQualificationCriterion('highValue')}>Prix élevés</button>
                <button className={`rounded-2xl border px-3 py-1.5 text-xs ${bulkQualCriteria.manySci ? 'border-blue-700 bg-blue-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`} onClick={() => toggleQualificationCriterion('manySci')}>Beaucoup de SCI</button>
                <button className={`rounded-2xl border px-3 py-1.5 text-xs ${bulkQualCriteria.highCommission ? 'border-blue-700 bg-blue-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`} onClick={() => toggleQualificationCriterion('highCommission')}>Commission élevée</button>
                {(bulkQualCriteria.highVolume || bulkQualCriteria.highValue || bulkQualCriteria.manySci || bulkQualCriteria.highCommission) && (
                  <button className="ml-2 text-xs text-slate-600 underline" onClick={() => { setBulkQualCriteria({ highVolume: false, highValue: false, manySci: false, highCommission: false }); setFilters((prev) => ({ ...prev, ventesMin: null, avgSaleMin: null, scisMin: null, commMin: null })); const count = computeCountWithFilters({ ...filters, ventesMin: null, avgSaleMin: null, scisMin: null, commMin: null }); showToast('success', `Qualification réinitialisée. ${count} copropriété(s) affichée(s).`); }}>Tout effacer</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Windows summary */}
      <div className="mb-4 rounded border p-4" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderColor: '#3b82f6' }}>
        {!windows && (<Badge mode="warning" text="Chargement du planning..." />)}
        {windows && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {(!isBoitage1Active || !isBoitage2Active) && (
              <div className="md:col-span-2">
                {!isBoitage1Active && (
                  <div className="mb-2"><Badge mode="warning" text="Boîtage 1 désactivé dans le planning." /></div>
                )}
                {!isBoitage2Active && (
                  <div className="mb-2"><Badge mode="warning" text="Boîtage 2 désactivé dans le planning." /></div>
                )}
              </div>
            )}
            {windows.map(w => {
              const planned = new Date(`${w.plannedDate}T00:00:00`);
              const start = new Date(planned);
              start.setDate(start.getDate() - 15);
              const end = new Date(planned);
              end.setDate(end.getDate() + 30);
              const now = new Date();
              const inWindow = now >= start && now <= end;
              const headerText = `Date boitage ${w.eventIndex} ${inWindow ? 'en cours' : 'prévue'}`;
              return (
                <div key={`${w.year}-${w.eventIndex}`} className="rounded-lg border bg-white p-4">
                  <div className="text-xs font-semibold text-slate-500 uppercase">{headerText}</div>
                  <div className="text-xl font-bold text-slate-800">{`${w.plannedLabel.toUpperCase()} ${planned.getFullYear()}`}</div>
                  <div className="mt-1 text-sm text-slate-700">Documents à prévoir: <span className="font-semibold">{w.docsPlanned.toLocaleString('fr-FR')}</span></div>
                  <div className="mt-3 flex gap-2">
                    <MarkDoneButton
                      plannedDate={w.plannedDate}
                      executedAt={w.executedAt}
                      onSubmit={async(executedIso) => {
                        if (!company?.id || !jwt) { return; }
                        try {
                          const resp = await fetch(patchCompanyBoitageWindow(company.id), {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
                            body: JSON.stringify({ year: Number(w.year), eventIndex: w.eventIndex, status: BOITAGE_STATUS.DONE, executedAt: executedIso }),
                          });
                          if (!resp.ok) { throw new Error(`${resp.status}`); }
                          showToast('success', 'Boîtage marqué comme effectué.');
                          fetchWindows();
                        } catch (e) {
                          console.error('Error bulk window mark done :', e);
                          showToast('error', "Erreur lors de la mise à jour 'effectué'.");
                        }
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* (moved controls above the table) */}
      </div>

      {/* Table */}
      {status === 'ERROR' && (
        <div className="p-6"><Badge mode="error" text={errorMessage || 'Erreur'} /></div>
      )}
      {status === 'PENDING' && (
        <div className="p-6"><Badge mode="warning" text="Chargement..." /></div>
      )}
      {status === 'SUCCESS' && (
        <div className="p-4">
          {/* Export buttons */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-600">{filteredRows.length} copropriété(s) avec boîtage</span>
            <button
              className="flex items-center gap-2 rounded bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              onClick={() => handleExportBoitage(1)}
              disabled={isExportingBoitage1}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {isExportingBoitage1 ? 'Export en cours...' : 'Exporter Boîtage 1 (Excel)'}
            </button>
            <button
              className="flex items-center gap-2 rounded bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              onClick={() => handleExportBoitage(2)}
              disabled={isExportingBoitage2}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {isExportingBoitage2 ? 'Export en cours...' : 'Exporter Boîtage 2 (Excel)'}
            </button>
          </div>
          <MUITable
            data={dataForTable}
            headers={[...headers, { title: 'Boîtage 1' }, { title: 'Boîtage 2' }]}
            tableClass="min-w-full bg-white border text-xs"
            sortableColumns={['Nb ventes', 'Montant total des ventes', 'Commission', 'Nb lots hab / local pro']}
            defaultSortKey="Nb ventes"
            defaultSortDirection="desc"
          />
        </div>
      )}

      {/* Selection popup removed; replaced by Documents panel */}
    </div>
  );
}

export default BoitageTransaction;


